import toastMessage from '@components/Toast';
import { CreateScheduleDeliverableParamsProps } from '@pages/ScheduledDeliverables/components/DeliverablesList/components/Actions/CreateEditModal/types';
import {
  ActivityLogParams,
  CloneScheduledDeliverableParam,
  DeleteReportParams,
  EmailDownloadParams,
  FetchTransactionsParams,
  fetchUploadedFilesPayload,
  markScheduledCompletedParam,
} from '@pages/ScheduledDeliverables/types';
import { AxiosInstance, AxiosResponse } from 'axios';

import { ScheduledDeliverablesAllTypes } from 'models/ScheduledDeliverables';

import { ApiResponse } from '../../models/ApiResponse';

export default class ScheduledDeliverablesAPI {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async fetchScheduledDeliverablesAll(): Promise<
    AxiosResponse<{ data: ScheduledDeliverablesAllTypes[] }>
  > {
    return this.client.get('/v2/reporting/scheduled-deliverables/');
  }

  async getScheduledDeliverable({ schedule_id }: { schedule_id: number }) {
    return this.client.get(
      `/v2/reporting/scheduled-deliverables/${schedule_id}/`,
    );
  }
  async markScheduledDeliverableCompleted(
    payload: markScheduledCompletedParam,
  ) {
    return this.client.post(
      '/v2/reporting/scheduled-deliverables/complete/',
      payload,
    );
  }

  async deleteScheduledDeliverable({ schedule_id }: { schedule_id: number }) {
    return this.client.delete(
      `/v2/reporting/scheduled-deliverables/${schedule_id}/`,
    );
  }
  async deleteScheduledDeliverables({
    selected_schedule_ids,
  }: {
    selected_schedule_ids: number[];
  }) {
    return this.client.post('/v2/reporting/scheduled-deliverables/delete/', {
      'scheduled-deliverable_id_list': selected_schedule_ids,
    });
  }

  async cloneScheduledDeliverable(payload: CloneScheduledDeliverableParam) {
    return this.client.post(
      '/v2/reporting/scheduled-deliverables/clone/',
      payload,
    );
  }
  async scheduledDeliverableTransactions(payload: FetchTransactionsParams) {
    return this.client.post(
      '/v2/reporting/scheduled-deliverables/transactions/',
      payload,
    );
  }

  async scheduleDeliverable(
    payload: CreateScheduleDeliverableParamsProps,
  ): Promise<ApiResponse<{ schedule_id: number }>> {
    const response = await this.client.post(
      '/v2/hedge-accounting/schedule-deliverable/',
      payload,
    );

    if (!response.data || typeof response.data.success !== 'boolean') {
      toastMessage.error('Invalid API response format');
    }

    return response.data;
  }

  async scheduleDeliverableNewTrades({ schedule_id }: { schedule_id: number }) {
    return this.client.post('/v2/reporting/scheduled-deliverables/new-trades', {
      schedule_id,
    });
  }

  async getClientEmails({ schedule_id }: { schedule_id: number }) {
    return this.client.get(
      `/v2/reporting/scheduled-deliverables/${schedule_id}/get-emails`,
    );
  }

  async sendEmailToClient(payload: EmailDownloadParams) {
    return this.client.post(
      '/v2/reporting/scheduled-deliverables/email-download/',
      payload,
    );
  }

  async getActivityLogs({ schedule_id }: { schedule_id: number }) {
    return this.client.get(
      `/v2/reporting/scheduled-deliverables/${schedule_id}/activity`,
    );
  }

  async newActivityLog(payload: ActivityLogParams) {
    return this.client.post(
      '/v2/reporting/scheduled-deliverables/activity/',
      payload,
    );
  }

  async getReportListDownload({ schedule_id }: { schedule_id: number }) {
    return this.client.get(
      `/v2/reporting/scheduled-deliverables/${schedule_id}/downloads/`,
    );
  }

  async deleteReportDownload({
    schedule_id,
    download_ids,
  }: DeleteReportParams) {
    return this.client.post(
      `/v2/reporting/scheduled-deliverables/${schedule_id}/downloads/delete/`,
      { schedule_id, download_ids },
    );
  }

  async sendUploadedReportsFromS3(payload: fetchUploadedFilesPayload) {
    return this.client.post(
      `/v2/reporting/scheduled-deliverables/${payload.schedule_id}/downloads/register/`,
      payload,
    );
  }

  async getScheduledDeliverablesTransactions({
    trade_id,
  }: {
    trade_id: number;
  }) {
    return this.client.get(
      `/v2/reporting/scheduled-deliverables/transaction/${trade_id}`,
    );
  }

  async getScheduledDeliverablesTestReports({
    schedule_id,
  }: {
    schedule_id: number;
  }) {
    return this.client.get(
      `/v2/hedge-accounting/automated-validations/precheck-runs/?schedule_id=${schedule_id}`,
    );
  }

  async getScheduledDeliverablesTestReport({
    report_id,
  }: {
    report_id: number;
  }) {
    return this.client.get(
      `/v2/hedge-accounting/automated-validations/precheck-runs/${report_id}/`,
    );
  }
}
